// Formatting that changes how schedule text reads, and so counts as a change when two versions are
// compared. Font size, colour, font family, highlight colour, alignment and indent are left out on
// purpose: the PDF strips the first four, and pasted text is full of all of them.
//
// Shared by the change detection (mark-formatting-structure.mjs, which runs on client and server)
// and the word highlighting (mark-formatted-words.js), so both always agree on what changed.
const TAG_FORMATS = {
    b: "bold",
    strong: "bold",
    i: "italic",
    em: "italic",
    u: "underline",
    ins: "underline",
    s: "strike",
    strike: "strike",
    del: "strike",
    sup: "sup",
    sub: "sub",
    blockquote: "quote",
    pre: "pre",
    td: "cell",
    th: "cell",
}

const FORMAT_ORDER = ["heading", "quote", "pre", "cell", "link", "bold", "italic", "underline", "strike", "sup", "sub"]

export const EMPTY_FORMATTING = Object.freeze({})

/**
 * Returns the formatting in force inside an element, given the formatting of its parent.
 * Equivalent tags (b/strong, i/em) give the same result, and an inline style can switch a format
 * off again, as Google Docs does with `<b style="font-weight:normal">`.
 *
 * @param {Record<string, string|boolean>} state formatting of the parent element
 * @param {string} tagName
 * @param {(name: string) => string|null|undefined} getAttribute
 * @returns {Record<string, string|boolean>}
 */
export function applyElementFormatting(state, tagName, getAttribute) {
    const tag = `${tagName ?? ""}`.toLowerCase()
    let next = state

    const format = TAG_FORMATS[tag]
    if (format) next = withFormat(next, format, true)
    if (/^h[1-6]$/.test(tag)) next = withFormat(next, "heading", tag)
    if (tag === "a") {
        const href = normaliseHref(getAttribute("href"))
        if (href) next = withFormat(next, "link", href)
    }

    const style = getAttribute("style")
    if (style) next = applyStyleFormatting(next, style)

    return next
}

/**
 * A stable text key for a formatting state; "" when the text is unformatted.
 *
 * @param {Record<string, string|boolean>} state
 * @returns {string}
 */
export function getFormattingKey(state) {
    return FORMAT_ORDER.filter((format) => state[format])
        .map((format) => (state[format] === true ? format : `${format}=${state[format]}`))
        .join("+")
}

/**
 * Applies the formatting set by an inline style. A span can be bold with no `<b>`, and a `<b>` can be
 * made plain again with `font-weight:normal`. Only weight, style, decoration
 * and vertical-align count; everything else is noise. Bold and italic can be switched off here.
 * Underline, strike, sup and sub can only be switched on.
 *
 * @param {Record<string, string|boolean>} state formatting so far, including the element's own tag
 * @param {string} style the element's style attribute
 * @returns {Record<string, string|boolean>}
 */
function applyStyleFormatting(state, style) {
    let next = state
    for (const declaration of `${style}`.split(";")) {
        const [property, ...rest] = declaration.split(":")
        const name = property?.trim().toLowerCase()
        const value = rest.join(":").trim().toLowerCase()
        if (!name || !value) continue

        if (name === "font-weight") {
            if (/^(bold|bolder|[6-9]00)\b/.test(value)) next = withFormat(next, "bold", true)
            else if (/^(normal|lighter|[1-5]00)\b/.test(value)) next = withFormat(next, "bold", false)
        } else if (name === "font-style") {
            if (/^(italic|oblique)\b/.test(value)) next = withFormat(next, "italic", true)
            else if (/^normal\b/.test(value)) next = withFormat(next, "italic", false)
        } else if (name === "text-decoration" || name === "text-decoration-line") {
            if (value.includes("underline")) next = withFormat(next, "underline", true)
            if (value.includes("line-through")) next = withFormat(next, "strike", true)
        } else if (name === "vertical-align") {
            if (value === "super") next = withFormat(next, "sup", true)
            else if (value === "sub") next = withFormat(next, "sub", true)
        }
    }
    return next
}

function withFormat(state, format, value) {
    if ((state[format] ?? false) === value) return state
    return { ...state, [format]: value }
}

function normaliseHref(href) {
    return `${href ?? ""}`.trim().replace(/&amp;/gi, "&").replace(/\s+/g, "%20")
}
